import { useEffect } from 'react';
import useStore from 'store';
import {
  useActiveMessage,
  useActiveMessageGatedByPro,
} from 'store/selectors/message';

// Focus the element and scroll to it
function focusElement(element: HTMLElement | undefined) {
  if (element) {
    const contentElement = findContentElement(element);
    const isFocused = contentElement === document.activeElement;
    if (!contentElement) return;
    if (!isFocused) {
      // element.focus() doesn't work for contenteditable...
      if (contentElement.isContentEditable) {
        const range = window.document.createRange();
        range.setStart(contentElement, 0);
        range.setEnd(contentElement, 0);

        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        contentElement.focus();
      }
    }

    // Scroll the element to the center of the screen
    contentElement.scrollIntoView({
      block: 'center',
    });
  }
}

// For RichText, the ref is on the parent, not the contenteditable div.
// This looks at the children and returns the contenteditable div.
function findContentElement(element: HTMLElement): HTMLElement | undefined {
  if (element.isContentEditable) return element;
  if (element.tagName === 'SELECT') return element;
  const editableEl =
    element.querySelector('[contenteditable]') ||
    element.querySelector('textarea') ||
    element.querySelector('input') ||
    element.querySelector('select');
  if (editableEl instanceof HTMLElement) {
    return editableEl;
  }
}

export function useFocusActiveElement() {
  const skipNextElementFocus = useStore.useSkipNextElementFocus();
  const setSkipNextElementFocus = useStore.useSetSkipNextElementFocus();
  const message = useActiveMessageGatedByPro();
  const srId = message ? message.srId : undefined;

  useEffect(() => {
    if (skipNextElementFocus) {
      // Don't focus the element and set the skip state back to false
      setSkipNextElementFocus(false);
    } else {
      if (srId) {
        const element = useStore.getState().elements.get(srId);
        if (element) {
          focusElement(element);
        }
      }
    }
  }, [srId]);
}
